import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../services/api";
import useAuthStore from "../store/authStore";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Loader2, Plus, Truck, Users, Inbox, Package, Camera } from "lucide-react";
import ProofActionDialog from "../components/orders/ProofActionDialog";
import ProofGallery from "../components/ProofGallery";

const COURIER_OPTIONS = [
  { id: "delhivery", name: "Delhivery" },
  { id: "ekart", name: "Ekart Logistics" },
  { id: "dtdc", name: "DTDC" },
  { id: "xpressbees", name: "XpressBees" },
];

export default function MicroHubDashboard() {
  const params = useParams();
  const { user } = useAuthStore();
  const [hubId, setHubId] = useState(params.hubId || "");
  const [dash, setDash] = useState(null);
  const [storage, setStorage] = useState(null);
  const [orders, setOrders] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staffOpen, setStaffOpen] = useState(false);
  const [handoffOpen, setHandoffOpen] = useState(null);   // order doc OR null
  const [handoffForm, setHandoffForm] = useState({ courier_partner: "delhivery", courier_awb: "", note: "" });
  const [proofTarget, setProofTarget] = useState(null);   // {order, kind}
  const [staffForm, setStaffForm] = useState({ name: "", email: "", phone: "", password: "", role: "agent", worker_type: "gig_pickup" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      if (!hubId) {
        try {
          const { data: me } = await api.get("/hierarchy/me");
          if (me.hub_scope && me.hub_scope.length > 0) {
            setHubId(me.hub_scope[0]);
          }
        } catch { /* */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    if (!hubId) { setLoading(false); return; }
    try {
      const [{ data: d }, { data: s }, { data: ord }, { data: st }] = await Promise.all([
        api.get(`/hierarchy/hubs/${hubId}/dashboard`),
        api.get(`/hierarchy/hubs/${hubId}/storage`),
        api.get(`/hierarchy/hubs/${hubId}/orders`, { params: { limit: 50 } }),
        api.get(`/hierarchy/hubs/${hubId}/staff`),
      ]);
      setDash(d); setStorage(s); setOrders(ord.orders || []); setStaff(st.staff || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load micro hub");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (hubId) load(); }, [hubId]);

  // Sprint 71 — light polling + instant WS fan-out from DashboardLayout.
  // The layout owns the single per-user WS connection and re-emits hub
  // events on the window so any hub dashboard mounted refetches in real-time.
  useEffect(() => {
    if (!hubId) return;
    const id = setInterval(() => load(), 15000);
    const onHubEvt = (e) => {
      const d = e.detail || {};
      const ids = [d.current_hub_id, d.origin_hub_id, d.destination_hub_id].filter(Boolean);
      if (ids.includes(hubId)) load();
    };
    window.addEventListener("pickngo:hub_order_event", onHubEvt);
    return () => {
      clearInterval(id);
      window.removeEventListener("pickngo:hub_order_event", onHubEvt);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubId]);

  const submitStaff = async () => {
    if (!staffForm.name || !staffForm.email || !staffForm.password) {
      toast.error("Name, email, password required"); return;
    }
    setSaving(true);
    try {
      await api.post(`/hierarchy/hubs/${hubId}/staff/create`, staffForm);
      toast.success("Worker added");
      setStaffOpen(false);
      setStaffForm({ name: "", email: "", phone: "", password: "", role: "agent", worker_type: "gig_pickup" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  const submitHandoff = async () => {
    if (!handoffForm.courier_awb.trim()) {
      toast.error("AWB / waybill is required"); return;
    }
    setSaving(true);
    try {
      await api.post(`/courier/handoff/${handoffOpen.tracking_id}`, {
        courier_partner: handoffForm.courier_partner,
        courier_awb: handoffForm.courier_awb.trim().toUpperCase(),
        note: handoffForm.note || null,
      });
      toast.success(`Handed to ${handoffForm.courier_partner} — AWB ${handoffForm.courier_awb}`);
      setHandoffOpen(null);
      setHandoffForm({ courier_partner: "delhivery", courier_awb: "", note: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Handoff failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 flex items-center gap-3"><Loader2 className="animate-spin h-6 w-6 text-orange-500" /> Loading...</div>;
  if (!hubId) return (
    <div className="p-8 max-w-3xl mx-auto">
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-amber-800">
        <h3 className="font-semibold mb-2">No Micro Hub assigned</h3>
        <p className="text-sm">Ask your Main Hub Manager to attach you to a Micro Hub.</p>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="microhub-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Inbox className="h-7 w-7 text-blue-500" />
            {dash?.hub?.name} <span className="text-base text-slate-400">· {dash?.hub?.hub_type}</span>
          </h1>
          <p className="text-slate-500 mt-1">{dash?.hub?.city} — {dash?.hub?.address}</p>
        </div>
        <Button onClick={() => setStaffOpen(true)} className="bg-blue-500" data-testid="microhub-add-staff">
          <Plus className="h-4 w-4 mr-2" /> Add Worker
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Kpi label="Today's pickups" value={dash?.orders?.today ?? 0} />
        <Kpi label="Pending" value={dash?.orders?.pending ?? 0} />
        <Kpi label="In-transit" value={dash?.orders?.in_transit ?? 0} />
        <Kpi label="Delivered" value={dash?.orders?.delivered ?? 0} />
        <Kpi label="Workers" value={staff.length} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Storage Bins (derived from orders at hub)">
          <BinRow name="Awaiting line-haul" docs={storage?.bins?.awaiting_linehaul || []} />
          <BinRow name="In line-haul" docs={storage?.bins?.in_linehaul || []} />
          <BinRow name="Awaiting dispatch" docs={storage?.bins?.awaiting_dispatch || []} />
          <BinRow name="Out for delivery" docs={storage?.bins?.out_for_delivery || []} />
        </Card>

        <Card title={`Workers (${staff.length})`}>
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Worker</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {staff.map((s) => (
                <TableRow key={s._id}>
                  <TableCell className="font-medium">{s.name}<div className="text-xs text-slate-400">{s.email}</div></TableCell>
                  <TableCell className="text-xs">{s.role}</TableCell>
                  <TableCell className="text-xs">{s.worker_type || "—"}</TableCell>
                  <TableCell>{s.is_online ? <span className="text-green-600 text-xs">online</span> : <span className="text-slate-400 text-xs">offline</span>}</TableCell>
                </TableRow>
              ))}
              {staff.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6">No workers yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Card title={`All orders at this hub (${orders.length})`} className="mt-6">
        <Table>
          <TableHeader><TableRow><TableHead>Tracking</TableHead><TableHead>Status</TableHead><TableHead>Weight</TableHead><TableHead>Proofs</TableHead><TableHead>Courier</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {orders.slice(0, 25).map((o) => (
              <TableRow key={o.tracking_id}>
                <TableCell className="font-mono text-xs">{o.tracking_id}</TableCell>
                <TableCell><span className="px-2 py-0.5 rounded-full bg-slate-100 text-xs">{o.status}</span></TableCell>
                <TableCell className="text-xs">{o.package_weight ? `${o.package_weight} kg` : "—"}</TableCell>
                <TableCell>
                  <ProofGallery order={o} size="sm" />
                </TableCell>
                <TableCell className="text-xs">
                  {o.courier_partner ? (
                    <span className="inline-flex items-center gap-1">
                      <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">{o.courier_partner}</span>
                      <span className="font-mono text-slate-500">{o.courier_awb}</span>
                    </span>
                  ) : <span className="text-slate-400 italic">not handed off</span>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1 flex-wrap justify-end">
                    {/* Sprint 72 — packing photo at origin micro hub */}
                    {(o.status === "at_origin_hub" || o.current_hub_id === hubId) && !o.packing_photo && (
                      <Button size="sm" variant="outline" onClick={() => setProofTarget({ order: o, kind: "packing" })} data-testid={`pack-btn-${o.tracking_id}`}>
                        <Camera className="h-3.5 w-3.5 mr-1" /> Pack & photo
                      </Button>
                    )}
                    {/* Sprint 72 — destination arrival photo */}
                    {(o.status === "at_dest_hub" || o.destination_hub_id === hubId) && !o.destination_arrival_photo && (
                      <Button size="sm" variant="outline" onClick={() => setProofTarget({ order: o, kind: "destination_arrival" })} data-testid={`arrived-btn-${o.tracking_id}`}>
                        <Camera className="h-3.5 w-3.5 mr-1" /> Arrived
                      </Button>
                    )}
                    {!o.courier_partner && (
                      <Button size="sm" variant="outline" onClick={() => setHandoffOpen(o)} data-testid={`handoff-btn-${o.tracking_id}`}>
                        <Truck className="h-3.5 w-3.5 mr-1" /> Hand to courier
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {orders.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">No orders yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={staffOpen} onOpenChange={setStaffOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Worker to {dash?.hub?.name}</DialogTitle>
            <DialogDescription>Creates a gig worker or ops staff user at this micro hub.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} data-testid="mu-staff-name" /></div>
            <div><Label>Email</Label><Input type="email" value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} data-testid="mu-staff-email" /></div>
            <div><Label>Phone</Label><Input value={staffForm.phone} onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })} /></div>
            <div><Label>Password</Label><Input type="password" value={staffForm.password} onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })} data-testid="mu-staff-password" /></div>
            <div>
              <Label>Role</Label>
              <select className="w-full border rounded-lg p-2" value={staffForm.role} onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })} data-testid="mu-staff-role">
                <option value="agent">Gig Worker (agent)</option>
                <option value="ops_staff">Ops Staff</option>
              </select>
            </div>
            <div className="col-span-2">
              <Label>Worker type</Label>
              <select className="w-full border rounded-lg p-2" value={staffForm.worker_type} onChange={(e) => setStaffForm({ ...staffForm, worker_type: e.target.value })}>
                <option value="gig_pickup">Gig Pickup</option>
                <option value="gig_delivery">Gig Delivery</option>
                <option value="packer">Packer</option>
                <option value="loader">Loader</option>
                <option value="helper">Helper</option>
              </select>
            </div>
          </div>
          <Button onClick={submitStaff} disabled={saving} className="mt-3 w-full bg-blue-500" data-testid="mu-staff-submit">
            {saving ? <Loader2 className="animate-spin h-4 w-4" /> : "Create worker"}
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog open={!!handoffOpen} onOpenChange={(o) => !o && setHandoffOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hand to courier</DialogTitle>
            <DialogDescription>
              {handoffOpen ? (
                <>Order <span className="font-mono font-semibold text-slate-700">{handoffOpen.tracking_id}</span> — pick the courier and enter their AWB.</>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Courier</Label>
              <select className="w-full border rounded-lg p-2" value={handoffForm.courier_partner}
                      onChange={(e) => setHandoffForm({ ...handoffForm, courier_partner: e.target.value })}
                      data-testid="handoff-courier-select">
                {COURIER_OPTIONS.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Courier AWB / Waybill</Label>
              <Input value={handoffForm.courier_awb}
                     onChange={(e) => setHandoffForm({ ...handoffForm, courier_awb: e.target.value.toUpperCase() })}
                     placeholder="e.g. 1234567890123"
                     className="font-mono"
                     data-testid="handoff-awb-input" />
              <p className="text-xs text-slate-400 mt-1">This stays internal. Customers continue tracking with the PickNGo ID.</p>
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input value={handoffForm.note}
                     onChange={(e) => setHandoffForm({ ...handoffForm, note: e.target.value })}
                     placeholder="e.g. Pickup runner: Ravi" />
            </div>
          </div>
          <Button onClick={submitHandoff} disabled={saving} className="mt-3 w-full bg-blue-500" data-testid="handoff-submit">
            {saving ? <Loader2 className="animate-spin h-4 w-4" /> : "Confirm handoff"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Sprint 72 — Proof of packing / destination arrival */}
      <ProofActionDialog
        target={proofTarget}
        onClose={() => setProofTarget(null)}
        onDone={load}
      />
    </div>
  );
}

function Kpi({ label, value }) {
  return <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm"><div className="text-xs uppercase tracking-wider text-slate-500">{label}</div><div className="text-xl font-bold mt-1">{value}</div></div>;
}
function Card({ title, children, className = "" }) {
  return <div className={`bg-white rounded-2xl border border-slate-100 p-5 shadow-sm ${className}`}><h3 className="font-semibold text-slate-800 mb-3">{title}</h3>{children}</div>;
}
function BinRow({ name, docs }) {
  return (
    <div className="border border-slate-100 rounded-xl p-3 mb-2">
      <div className="flex items-center justify-between mb-1">
        <span className="font-medium text-sm">{name}</span>
        <span className="text-xs text-slate-400">{docs.length} item(s)</span>
      </div>
      <div className="flex flex-wrap gap-1">
        {docs.slice(0, 12).map((d) => (
          <span key={d.tracking_id} className="text-[10px] bg-slate-100 rounded px-2 py-0.5 font-mono" title={d.tracking_id}>{d.tracking_id}</span>
        ))}
        {docs.length === 0 && <span className="text-xs text-slate-400 italic">empty</span>}
      </div>
    </div>
  );
}
